import React, { useEffect } from 'react';
import { Autocomplete, TextField, FormHelperText, Checkbox, FormControl } from '@mui/material';
import { Controller } from 'react-hook-form';
import { FixedSizeList } from 'react-window';

const ComplexAutocomplete = React.forwardRef(({
    name,
    label,
    defaultValue = null, // Default value for single-select (can be an object or value)
    data, // Array of objects with keys for value and display
    error,
    control,
    rules,
    variant = "outlined",
    margin = "dense",
    onChange: onChangeFromParent,
    valueKey, // The key to be used for the value (e.g., 'subeKodu')
    displayKey, // The key to be used for the display text (e.g., 'subeAdi')
    separator = ' - ',
    disabled = false,
    multiple = false, // Enable multi-select
    ...props
}, ref) => {

    useEffect(() => { }, [data, control]);

    // Get label based on displayKey, which can be an array
    const getLabel = (item) => {
        if (!item) return '';
        if (Array.isArray(displayKey)) {
            return displayKey.map(field => item[field]).join(separator);
        }
        return item[displayKey] ?? "";
    };

    // Helper to get the value of an option based on the valueKey
    const getValue = (item) => {
        if (!item) return null;
        return item[valueKey]; // Return the value based on valueKey
    };

    return (
        <FormControl fullWidth size="small" variant={variant} margin={margin} error={!!error}>
            <Controller
                name={name}
                control={control}
                rules={rules}
                defaultValue={defaultValue || (multiple ? [] : null)} // Ensure correct default value
                render={({ field }) => (
                    <Autocomplete
                        {...field}
                        ref={ref}
                        multiple={multiple}
                        options={data}
                        getOptionLabel={(option) => getLabel(option) || ''}
                        isOptionEqualToValue={(option, value) => {
                            // When value is a primitive (subeKodu), compare directly
                            if (typeof value === 'string' || typeof value === 'number') {
                                return getValue(option) === value;
                            }
                            // When value is an object, compare based on valueKey
                            return getValue(option) === getValue(value);
                        }}
                        value={multiple ? field.value || [] : field.value || null}
                        onChange={(event, value) => {
                            field.onChange(value);
                            if (onChangeFromParent) {
                                onChangeFromParent(value);
                            }
                        }}
                        disabled={disabled}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={label}
                                variant={variant}
                                margin={margin}
                                error={!!error}
                            />
                        )}
                        renderOption={(props, option, { selected }) => (
                            <li {...props}>
                                {multiple && <Checkbox checked={selected} />}
                                {getLabel(option)}
                            </li>
                        )}
                        ListboxComponent={props => (
                            <FixedSizeList
                                height={300}
                                width="100%"
                                itemSize={36}
                                itemCount={data.length}
                                {...props}
                            >
                                {({ index, style }) => (
                                    <div style={style} {...props}>
                                        {props.children[index]}
                                    </div>
                                )}
                            </FixedSizeList>
                        )}
                        {...props}
                    />
                )}
            />
            {error && <FormHelperText error>{error.message}</FormHelperText>}
        </FormControl>
    );
});

export default ComplexAutocomplete;
